import { LogIn } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Navigate } from "react-router-dom";
import useSWR, { mutate } from "swr";
import { apiFetch, jsonBody, swrFetcher } from "@/web/api";
import brandLogo from "@/web/assets/brand-logo.png";
import { BRAND_NAME } from "@/web/lib/brand";
import { Button } from "@/web/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/web/components/ui/card";
import { Input } from "@/web/components/ui/input";
import { Label } from "@/web/components/ui/label";
import { Loading } from "@/web/components/ui/loading";

export function LoginPage() {
  const { data, isLoading } = useSWR<{ setup_completed: boolean; totp_enabled: boolean }>("/admin-api/setup/status", swrFetcher);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  if (isLoading) return <Loading />;
  if (!data?.setup_completed) return <Navigate to="/setup" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await apiFetch("/admin-api/login", { method: "POST", ...jsonBody({ username: "admin", password, code }) });
      // 整页跳转：确保新会话 cookie 已落盘，并清空 SWR 内存中可能存在的 401 缓存，
      // 避免「登录成功后又被弹回登录页」需要登录两次的问题。
      await mutate(() => true, undefined, { revalidate: false });
      window.location.assign("/dashboard");
      return;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "登录失败");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-3">
          <img src={brandLogo} alt="" className="h-10 w-10 shrink-0 object-contain" />
          <div><div className="text-[17px] font-semibold">{BRAND_NAME}</div><div className="text-xs text-muted">支付运维控制台</div></div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>管理员登录</CardTitle>
            <CardDescription>{data?.totp_enabled ? "用户名固定为 admin，需验证密码与动态码。" : "单用户部署，用户名固定为 admin。"}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={submit}>
              <div className="space-y-2"><Label htmlFor="login-user">用户名</Label><Input id="login-user" value="admin" disabled /></div>
              <div className="space-y-2"><Label htmlFor="login-password">密码</Label><Input id="login-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus autoComplete="current-password" required /></div>
              {data?.totp_enabled ? (
                <div className="space-y-2">
                  <Label htmlFor="login-code">双重验证动态码</Label>
                  <Input
                    id="login-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="6 位数字"
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                    className="font-mono tracking-[0.3em]"
                    required
                  />
                  <p className="text-xs leading-5 text-muted">打开身份验证器 App 获取当前 6 位动态码。</p>
                </div>
              ) : null}
              {error ? <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert">{error}</div> : null}
              <Button className="w-full" disabled={submitting}>{submitting ? "正在验证…" : "登录"}<LogIn /></Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
